import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileImage, FileText, Download, Loader2, AlertCircle, CheckCircle2, Lock, X, Files } from 'lucide-react';
import { toast } from 'sonner';
import api, { departmentsApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const AddLogoToPdfPage = () => {
  const { user } = useAuth();
  const [pdfFiles, setPdfFiles] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [position, setPosition] = useState('top-right');
  const [logoWidth, setLogoWidth] = useState('120');
  const [logoHeight, setLogoHeight] = useState('60');
  const [excludedPages, setExcludedPages] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  
  // Access control
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    checkPdfToolsAccess();
  }, []);

  const checkPdfToolsAccess = async () => {
    try {
      // Admin always has access
      if (user?.role === 'admin') {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }
      
      const accessData = await departmentsApi.getUserDepartmentAccess();
      setHasAccess(accessData.hasPdfToolsAccess || false);
    } catch (error) {
      console.error('Failed to check PDF tools access:', error);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const handlePdfChange = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => file.type === 'application/pdf');
    
    if (validFiles.length !== files.length) {
      toast.error('Some files were not PDFs and were skipped');
    }
    
    if (validFiles.length > 0) {
      setPdfFiles(prev => [...prev, ...validFiles]);
      setMessage('');
    }
  };

  const removePdfFile = (index) => {
    setPdfFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
      setLogoFile(file);
      setMessage('');
    } else if (file) {
      toast.error('Please select a PNG or JPEG image');
    }
  };

  const handleSubmit = async () => {
    setMessage('');
    setIsError(false);

    if (pdfFiles.length === 0 || !logoFile) {
      setMessage('Please upload at least one PDF and a logo file.');
      setIsError(true);
      return;
    }

    const formData = new FormData();
    
    // Append all PDF files
    pdfFiles.forEach(file => {
      formData.append('pdfs', file);
    });
    
    formData.append('logo', logoFile);
    formData.append('position', position);
    formData.append('logoWidth', logoWidth);
    formData.append('logoHeight', logoHeight);
    formData.append('excludedPages', excludedPages);

    try {
      setLoading(true);

      const response = await api.post('/pdf/add-logo', formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Check if response is an error (JSON)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Failed to process PDF');
      }

      // Determine file type from response
      const isZip = response.data.type === 'application/zip';
      const blob = new Blob([response.data], { 
        type: isZip ? 'application/zip' : 'application/pdf' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      if (isZip) {
        link.download = 'pdfs-with-logo.zip';
      } else {
        const originalName = pdfFiles[0]?.name || 'document.pdf';
        link.download = originalName.replace('.pdf', '-with-logo.pdf');
      }
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const successMsg = pdfFiles.length > 1 
        ? `${pdfFiles.length} PDFs processed successfully! ZIP file downloaded.`
        : 'Logo added successfully! File downloaded.';
      
      setMessage(successMsg);
      setIsError(false);
      toast.success(successMsg);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Something went wrong.';
      setMessage(errorMsg);
      setIsError(true);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const clearFiles = () => {
    setPdfFiles([]);
    setLogoFile(null);
    setMessage('');
    // Reset file inputs
    const pdfInput = document.getElementById('pdf-input');
    const logoInput = document.getElementById('logo-input');
    if (pdfInput) pdfInput.value = '';
    if (logoInput) logoInput.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Loading state
  if (checkingAccess) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#6a5da4]" />
      </div>
    );
  }

  // Access denied
  if (!hasAccess) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add Logo to PDF</h1>
        </div>
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Access Restricted</h2>
              <p className="text-gray-600">
                You do not have access to PDF Tools. Please contact your administrator to enable this feature for your account.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Add Logo to PDF</h1>
        <p className="text-gray-600 mt-1">
          Upload one or more PDFs and a logo image. The logo will be added to every page except the pages you choose to skip.
          {pdfFiles.length > 1 && <span className="text-[#6a5da4] font-medium"> Multiple files will be returned as a ZIP archive.</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upload Files</CardTitle>
            <CardDescription>Select your PDF documents and logo image</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* PDF Upload */}
            <div className="space-y-2">
              <Label htmlFor="pdf-input">PDF Files (multiple allowed)</Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-[#6a5da4] transition-colors">
                <input
                  id="pdf-input"
                  type="file"
                  accept="application/pdf"
                  multiple
                  onChange={handlePdfChange}
                  className="hidden"
                  data-testid="pdf-input"
                />
                <label
                  htmlFor="pdf-input"
                  className="flex flex-col items-center gap-2 cursor-pointer"
                >
                  <Files className={`w-10 h-10 ${pdfFiles.length > 0 ? 'text-[#6a5da4]' : 'text-gray-400'}`} />
                  {pdfFiles.length > 0 ? (
                    <span className="text-sm font-medium text-[#6a5da4]">
                      {pdfFiles.length} PDF{pdfFiles.length > 1 ? 's' : ''} selected
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">Click to upload PDFs (multiple allowed)</span>
                  )}
                </label>
              </div>
              
              {/* Selected PDFs List */}
              {pdfFiles.length > 0 && (
                <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                  {pdfFiles.map((file, index) => (
                    <div 
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2 overflow-hidden">
                        <FileText className="w-4 h-4 text-[#6a5da4] flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{file.name}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          ({formatFileSize(file.size)})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePdfFile(index)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                        data-testid={`remove-pdf-${index}`}
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Logo Upload */}
            <div className="space-y-2">
              <Label htmlFor="logo-input">Logo Image (PNG or JPEG)</Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-[#6a5da4] transition-colors">
                <input
                  id="logo-input"
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleLogoChange}
                  className="hidden"
                  data-testid="logo-input"
                />
                <label
                  htmlFor="logo-input"
                  className="flex flex-col items-center gap-2 cursor-pointer"
                >
                  <FileImage className={`w-10 h-10 ${logoFile ? 'text-[#6a5da4]' : 'text-gray-400'}`} />
                  {logoFile ? (
                    <span className="text-sm font-medium text-[#6a5da4]">{logoFile.name}</span>
                  ) : (
                    <span className="text-sm text-gray-500">Click to upload logo</span>
                  )}
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Logo Settings</CardTitle>
            <CardDescription>Configure position and size</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Position */}
            <div className="space-y-2">
              <Label>Logo Position</Label>
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger data-testid="position-select">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="top-left">Top Left</SelectItem>
                  <SelectItem value="top-right">Top Right</SelectItem>
                  <SelectItem value="bottom-left">Bottom Left</SelectItem>
                  <SelectItem value="bottom-right">Bottom Right</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Size */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="logo-width">Logo Width (px)</Label>
                <Input
                  id="logo-width"
                  type="number"
                  value={logoWidth}
                  onChange={(e) => setLogoWidth(e.target.value)}
                  min="10"
                  max="500"
                  data-testid="logo-width"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="logo-height">Logo Height (px)</Label>
                <Input
                  id="logo-height"
                  type="number"
                  value={logoHeight}
                  onChange={(e) => setLogoHeight(e.target.value)}
                  min="10"
                  max="500"
                  data-testid="logo-height"
                />
              </div>
            </div>

            {/* Excluded Pages */}
            <div className="space-y-2">
              <Label htmlFor="excluded-pages">Exclude Pages (optional)</Label>
              <Input
                id="excluded-pages"
                placeholder="e.g., 1, 3, 5-7"
                value={excludedPages}
                onChange={(e) => setExcludedPages(e.target.value)}
                data-testid="excluded-pages"
              />
              <p className="text-xs text-gray-500">
                Enter page numbers to skip. Use commas for individual pages and hyphens for ranges.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={handleSubmit}
          disabled={loading || pdfFiles.length === 0 || !logoFile}
          className="bg-[#6a5da4] hover:bg-[#5a4d94] flex-1 sm:flex-none"
          data-testid="process-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing {pdfFiles.length} file{pdfFiles.length > 1 ? 's' : ''}...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              {pdfFiles.length > 1 
                ? `Process ${pdfFiles.length} PDFs & Download ZIP`
                : 'Add Logo & Download PDF'
              }
            </>
          )}
        </Button>
        
        {(pdfFiles.length > 0 || logoFile) && (
          <Button
            variant="outline"
            onClick={clearFiles}
            disabled={loading}
            data-testid="clear-button"
          >
            Clear All Files
          </Button>
        )}
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`flex items-center gap-2 p-4 rounded-lg ${
            isError
              ? 'bg-red-50 border border-red-200 text-red-700'
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}
          data-testid="status-message"
        >
          {isError ? (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          )}
          <span>{message}</span>
        </div>
      )}

      {/* Help Section */}
      <Card className="bg-gray-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-gray-900 mb-2">How to use</h3>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Upload one or more PDF documents you want to add a logo to</li>
            <li>Upload your logo image (PNG or JPEG format)</li>
            <li>Choose where you want the logo to appear on each page</li>
            <li>Adjust the logo size if needed</li>
            <li>Optionally exclude specific pages from having the logo</li>
            <li>Click the button to process and download</li>
            <li><strong>Multiple PDFs</strong> will be downloaded as a <strong>ZIP archive</strong></li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddLogoToPdfPage;
